/* eslint-disable prettier/prettier */
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { FiCalendar } from 'react-icons/fi';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { ENV } from '@environment';
import { isEqual } from 'lodash';

import { hotelsSearchParameters } from 'action-creators/hotels';
import DateRangePicker from 'components/DateRangePicker';
import BookingSelectDate, { RenderProps as BookingSelectRenderProps } from 'components/Selects/BookingSelectDate';
import BookingSelectLocation from 'components/Selects/BookingSelectLocation';
import BranchCompanySelect from 'components/Selects/BranchCompanySelect';
import TravelArrangerSelect from 'components/Selects/TravelArrangerSelect';
import AccommodationSelect from 'components/Selects/AccommodationSelect';
import HotelsLocationSearchContainer from 'containers/HotelsLocationSearch';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useUserAccount from 'hooks/useUserAccount';
import { GetAllBillingCompanyResponse } from 'models/billingCompany';
import { GroupedListModel } from 'models/groupedList';
import { TravelHotelContentLocationRequest } from 'models/hotelLocation';
import ResponseScreen, { BackgroundTypes } from 'screens/components/ResponseScreen';
import { amplitudelogEvent, SearchEvent } from 'services/amplitudeService';
import { Guest } from 'services/userGuestService';
import { TravelArrangerUsersAllowed } from 'services/userService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { dateFormatWeekend, daysCount } from 'utils/generic';
import { compareDates } from 'utils/time';

import { Container, Fields, Field, SearchButton, SearchButtonContainer } from './styles';
import { store } from 'store';
import { City } from 'models/city';
import CitiesSearchContainer from 'containers/CitiesSearch';

interface Props {
  defaultTravelers?: TravelArrangerUsersAllowed[];
  defaultDestinationPoint?: TravelHotelContentLocationRequest | City;
  defaultCheckInDate?: Date;
  defaultCheckOutDate?: Date;
  allowedTravelers: TravelArrangerUsersAllowed[];
  isLoadingAllowedTravelers: boolean;
  isHome?: boolean;
  onClose?: () => void;
  branchCompanies?: GetAllBillingCompanyResponse[];
  isLoadingBranchCompanies: boolean;
  defaultBranchCompany?: GetAllBillingCompanyResponse;
}

const travelersToGroupedModel = (travelers: TravelArrangerUsersAllowed[]): GroupedListModel[] =>
  travelers.map(
    (t) =>
      ({
        id: t.id,
        name: t.name,
        lastName: t.lastName,
        favorite: t.favorite,
        email: t.email,
        userGroupId: t.userGroupId,
        userGroupName: t.userGroupName,
        entity: t.guest ? 'guest' : 'user',
        disabled:
          t.userGroupId === undefined ||
          t.userGroupId === '' ||
          (t.userGroupId !== undefined &&
            t.userGroupId !== '' &&
            !t.hasDomesticFlightPolicies &&
            !t.hasDomesticHotelPolicies &&
            !t.hasDomesticVehiclePolicies &&
            !t.hasInternationalFlightPolicies &&
            !t.hasInternationalHotelPolicies &&
            !t.hasInternationalVehiclePolicies),
      } as GroupedListModel),
  );

const branchCompanyToGroupedModel = (branchCompany: GetAllBillingCompanyResponse): GroupedListModel => ({
  id: branchCompany.id,
  name: branchCompany.companyName,
});



const HotelsSearch = ({
  defaultTravelers,
  defaultDestinationPoint,
  defaultCheckInDate,
  defaultCheckOutDate,
  allowedTravelers,
  isLoadingAllowedTravelers,
  isHome = true,
  onClose,
  branchCompanies,
  isLoadingBranchCompanies,
  defaultBranchCompany,
}: Props) => {
  const history = useHistory();
  const location = useLocation<{ showNoResults?: boolean } | null>();
  const { t } = useStoreTranslation();
  const user = useUserAccount();
  const dispatch = useDispatch();

  const [showNoResults, setShowNoResults] = useState(location.state?.showNoResults || false);
  const [destinationPoint, setDestinationPoint] = useState<TravelHotelContentLocationRequest | City | undefined>(
    defaultDestinationPoint,
  );
  const [checkInDate, setCheckInDate] = useState<Date | undefined>(defaultCheckInDate);
  const [checkOutDate, setCheckOutDate] = useState<Date | undefined>(defaultCheckOutDate);
  //const [traveler, setTraveler] = useState<TravelArrangerUsersAllowed[] | undefined>(defaultTraveler);
  const [branchCompany, setBranchCompany] = useState<GetAllBillingCompanyResponse | undefined>(defaultBranchCompany);
  const [travelers, setTravelers] = useState<TravelArrangerUsersAllowed[] | undefined>(defaultTravelers);
  const [accommodationSelected, setAccommodationSelected] = useState<number>(1);

  useEffect(() => {
    history.replace({ ...history.location, state: undefined });
  }, [history]);

  useEffect(() => {
    setTravelers([]);
  }, []);

  useEffect(() => {
    setBranchCompany(defaultBranchCompany);
  }, [defaultBranchCompany]);

  const isValid = useMemo(() => {
    const hasTraveler = !user?.isTravelArranger || (user?.isTravelArranger && travelers && travelers.length > 0);
    return Boolean(destinationPoint && checkInDate && checkOutDate && hasTraveler);
  }, [user, travelers, destinationPoint, checkInDate, checkOutDate]);

  const isBranchCompanyValid = useMemo(() => {
    // TODO: Billing companies - descomentar para funcionamiento correcto
    return true;

    if (branchCompanies && branchCompanies.length > 0) {
      if (branchCompany === undefined) {
        return false;
      }
    }
    return true;
  }, [branchCompanies, branchCompany]);

  const transformedTravelerList: GroupedListModel[] = useMemo(
    () => travelersToGroupedModel(allowedTravelers),
    [allowedTravelers],
  );
  const transformedSelectedTraveler: GroupedListModel[] | undefined = useMemo(() => {
    if (!travelers) return undefined;
    return travelersToGroupedModel(travelers);
  }, [travelers]);

  const transformedSelectedBranchCompany: GroupedListModel | undefined = useMemo(() => {
    if (!branchCompany) return undefined;
    return branchCompanyToGroupedModel(branchCompany);
  }, [branchCompany]);

  const handleChangeDateSelect = (dates: { initialDate: Date; finalDate: Date }) => {
    setCheckInDate(dates?.initialDate);
    setCheckOutDate(dates?.finalDate);
  };

  const handleClickSearchButton = useCallback(async () => {
    if (destinationPoint && checkInDate && checkOutDate) {
      if (
        isEqual(destinationPoint, defaultDestinationPoint) &&
        compareDates(checkInDate, defaultCheckInDate) &&
        compareDates(checkOutDate, defaultCheckOutDate) &&
        isEqual(travelers, defaultTravelers) &&
        !isHome
      ) {
        if (onClose) onClose();
        return;
      }
      await dispatch(
        hotelsSearchParameters({
          destinationPoint,
          checkInDate,
          checkOutDate,
          travelers,
          branchCompany,
        }),
      );
      if (isHome) history.push('/corporate/hotels');
      else history.replace('/corporate/hotels');
    }

    const destinationId = (() => {
      if (!destinationPoint) return '';

      if ('id' in destinationPoint) {
        return destinationPoint.id.toString();
      } else {
        return destinationPoint?.code.toString();
      }
    })();

    //Amplitude
    const event: SearchEvent = {
      TRAVEL_PRODUCT_TYPE: 'HOTELS',
      TRAVEL_FROM: '',
      TRAVEL_TO: destinationId ?? '',
      TRAVEL_DESTINATION_TYPE: '',
      TRAVEL_TRIP_TYPE: '',
      TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), checkInDate).toString(),
      TRAVEL_DATE_FROM: checkInDate?.toString() ?? '',
      TRAVEL_DATE_TO: checkOutDate?.toString() ?? '',
      TRAVEL_TRIP_LENGTH: daysCount(checkInDate, checkOutDate).toString(),
      TRAVEL_PASSENGERS: '1',
      TRAVEL_ADULTS: '1',
      TRAVEL_CHILDREN: '0',
      TRAVEL_INFANTS: '0',
      TRAVEL_CLASS_OF_SERVICE: '',
      TRAVEL_ROOMS: '1',
      TRAVEL_WITH_BAGGAGE: '',
      TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
      SEARCHID: '',
    };

    amplitudelogEvent(amplitudeEvents.SELECT_TRAVEL_SEARCH, event, user);
  }, [
    branchCompany,
    checkInDate,
    checkOutDate,
    defaultCheckInDate,
    defaultCheckOutDate,
    defaultDestinationPoint,
    defaultTravelers,
    destinationPoint,
    dispatch,
    history,
    isHome,
    onClose,
    travelers,
    user,
  ]);

  const getBookingSelectValue = (data: TravelHotelContentLocationRequest | City | undefined) => {
    if (!data) return;

    if ('name' in data) {
      return {
        name: data.name,
        detail: data.description,
      };
    } else {
      return {
        name: data.related_city,
        detail: data.country,
      };
    }
  };

  const transformedBranchCompanies = (): GroupedListModel[] =>
    branchCompanies ? branchCompanies?.map((b) => ({ id: b.id, name: b.companyName })) : ([] as GroupedListModel[]);

  const renderDeparturePointSelectV3 = () => (
    <BookingSelectLocation
      label={t('hotelsSearch.destinationPoint.label')}
      detail={t('hotelsSearch.destinationPoint.detail')}
      value={getBookingSelectValue(destinationPoint)}
      onChange={(value, isCity?: boolean) => {
        setDestinationPoint(value as TravelHotelContentLocationRequest);
      }}
      testId="hotel-search-destination"
    >
      {(selectProps: BookingSelectRenderProps) => (
        <HotelsLocationSearchContainer
          {...selectProps}
          placeHolder={t('hotelsSearch.destinationPoint.modalSearchPlaceholder')}
        />
      )}
    </BookingSelectLocation>
  );

  const renderDeparturePointSelectV2 = () => (
    <BookingSelectLocation
      label={t('hotelsSearch.destinationPoint.label')}
      detail={t('hotelsSearch.destinationPoint.detail')}
      value={getBookingSelectValue(destinationPoint)}
      onChange={(value, isCity?: boolean) => {
        setDestinationPoint(value as City);
      }}
      testId="hotel-search-destination"
    >
      {(selectProps: BookingSelectRenderProps) => (
        <CitiesSearchContainer
          {...selectProps}
          placeHolder={t('hotelsSearch.destinationPoint.modalSearchPlaceholder')}
        />
      )}
    </BookingSelectLocation>
  );

  const renderCheckInDateSelect = () => (
    <BookingSelectDate
      label={t('hotelsSearch.checkInDate')}
      value={checkInDate ? dateFormatWeekend(checkInDate) : undefined}
      onChange={handleChangeDateSelect}
      icon={<FiCalendar />}
      testId="hotel-search-checkin-date"
    >
      {(selectProps: BookingSelectRenderProps) => (
        <DateRangePicker
          {...selectProps}
          minRange={1}
          type="check-in-out"
          currentInitialDate={checkInDate}
          currentFinalDate={checkOutDate}
        />
      )}
    </BookingSelectDate>
  );

  const renderCheckOutDateSelect = () => (
    <BookingSelectDate
      label={t('hotelsSearch.checkOutDate')}
      value={checkOutDate ? dateFormatWeekend(checkOutDate) : undefined}
      onChange={handleChangeDateSelect}
      icon={<FiCalendar />}
      testId="hotel-search-checkout-date"
    >
      {(selectProps: BookingSelectRenderProps) => (
        <DateRangePicker
          {...selectProps}
          minRange={1}
          type="check-in-out"
          editingInitialDate={false}
          canEditingInitialDate={true}
          currentInitialDate={checkInDate}
          currentFinalDate={checkOutDate}
        />
      )}
    </BookingSelectDate>
  );

  const handleAccommodation = (value: number) => {
    setAccommodationSelected(value);
    setTravelers([]);
  }

  const handleTravelerSelect = (items: GroupedListModel[]) => {
      const travelers = [];
      for (let index = 0; index < items.length; index++) {
        const item = items[index];
        const tempTraveler = allowedTravelers.find((traveler) => traveler.id === item.id);
  
        if (tempTraveler !== null) {
          travelers.push(tempTraveler);
        }
      }
      setTravelers(travelers);
    };

  const handleBranchCompanySelect = (item: GroupedListModel) => {
    if (branchCompanies) {
      setBranchCompany(branchCompanies?.find((branchCompany) => branchCompany.id === item.id));
    }

    return;
  };

  const handleCreateGuestTraveler = (guest: Guest) => {
    setTravelers([{
      guest: true,
      favorite: false,
      userId: guest.userId || '',
      id: guest.id || '',
      name: guest.name,
      lastName: guest.lastName,
      email: guest.email,
      birthDate: guest.birthDate,
      phoneNumber: guest.phoneNumber,
      country: guest.country,
      documentNumber: guest.documentNumber,
      gender: guest.gender,
      passportNumber: guest.passportNumber,
    }]);
  };

  const renderTravelerSelect = () => {
    if (!user) return;
    if (!user.isTravelArranger) return null;

    return (
      <>
        <TravelArrangerSelect
          title={t('travelArrangerSelector.placeholder')}
          buttonLabel={t('travelArrangerSelector.buttonLabel')}
          addGuestLabel={t('travelArrangerSelector.addGuest')}
          inviteAllowed={user.isGuestBookAllowed}
          options={transformedTravelerList}
          value={transformedSelectedTraveler}
          onChange={handleTravelerSelect}
          onCreateGuest={handleCreateGuestTraveler}
          isLoading={isLoadingAllowedTravelers}
          testId="hotel-search-traveler"
          singleSelect={false}
          maxSelectedItems={accommodationSelected}
          maxSelectedItemsByHotel={true}
        />
      </>
    );
  };

  const renderAccommodation = () => {
    if (!user) return;
    if (!user.isTravelArranger) return null;

    return <AccommodationSelect
      title={t('accommodationSelector.placeholder')}
      height='75px'
      value={1}
      isLoading={isLoadingAllowedTravelers}
      testId="hotel-search-accommodation"
      onChange={handleAccommodation}
    />
  };

  const renderBranchCompanySelect = () => {
    if (!user) return;
    if (!branchCompanies) return;
    if (branchCompanies.length <= 0) return;

    // TODO - Comentado para no mostrar, debe cambiar para BillingCompanies para no impactar Branch
    return;

    return (
      <BranchCompanySelect
        title={t('billingCompanySelector.placeholder')}
        buttonLabel={t('billingCompanySelector.buttonLabel')}
        options={transformedBranchCompanies()}
        value={transformedSelectedBranchCompany}
        onChange={handleBranchCompanySelect}
        isLoading={isLoadingBranchCompanies}
        testId="hotel-search-branch-company"
      />
    );
  };

  return (
    <>
      <Container>
        <Fields>
          <Field>
            {store.getState().companyConfiguration?.configuration?.menuView?.hotelSearchV2
              ? renderDeparturePointSelectV2()
              : renderDeparturePointSelectV3()}
          </Field>
          <Field columns={2}>
            {renderCheckInDateSelect()}
            {renderCheckOutDateSelect()}
          </Field>
          <Field>{renderAccommodation()}</Field>
          <Field>{renderTravelerSelect()}</Field>
          <Field>{renderBranchCompanySelect()}</Field>
        </Fields>
        <SearchButtonContainer>
          <SearchButton
            disabled={!isValid || isLoadingAllowedTravelers || !isBranchCompanyValid}
            onClick={handleClickSearchButton}
            value={t('hotelsSearch.searchButton')}
            testId="hotel-search-submit"
          />
        </SearchButtonContainer>
      </Container>

      <ResponseScreen
        show={showNoResults}
        text=""
        subText={t('hotelsSearch.searchError')}
        background={BackgroundTypes.Mountain}
        buttonOnClick={() => setShowNoResults(false)}
        buttonText={t('hotelsSearch.backButton')}
      />
    </>
  );
};

export default HotelsSearch;
